import React, { useContext, useState } from 'react';
import axios from 'axios';
import { ThemeProvider } from 'emotion-theming';
import * as yup from 'yup';
import { Formik } from 'formik';
import { ThemeContext } from '@ordermentum/ui-shared/utilities/theme';
import { Input } from '@ordermentum/ui-shared/atoms/Input';
import { MEDIA_QUERIES as M } from '@ordermentum/ui-shared/foundations/grid';
import {
  SignedOutContainer,
  ButtonWrapper,
} from '@ordermentum/ui-shared/templates/SignupFlowTemplate/styles';
import Banner from '@ordermentum/ui-shared/components/Banner';
import Button from '@ordermentum/ui-shared/components/Button/Button';
import Divider from '@ordermentum/ui-shared/components/Divider';
import S from '@ordermentum/ui-shared/foundations/size';
import RightSideContent from '@ordermentum/ui-shared/templates/SignupFlowTemplate/components/RightSideContent';
import Spinner from '@ordermentum/ui-shared/foundations/Spinner';

import { onGoogleButtonClick } from '../utilities';
import {
  ThemeStyled,
  getTheme,
  Content,
  InputContainer,
  ErrorContainer,
} from './styles';
import { Pages } from '..';
import { MFAAction } from '../../Forms/MFAForm';
import { history } from '../../../../lib/history';

type Props = {
  onPageChange: (page: Pages) => void;
};

const DividerContainer = ThemeStyled.div({
  margin: `${S.L} 0 ${S.XXS} 0`,
  [M.TABLET_PORTRAIT_AND_SMALLER]: {
    textTransform: 'uppercase',
  },
});

const GOOGLE_IMAGE_URL = `${process.env.IMAGE_URL}/google.svg`;

const SignInForm = (props: Props) => {
  const theme = getTheme(useContext(ThemeContext));
  const { onPageChange } = props;
  const [formError, setFormError] = useState('');
  const [isFormLoading, setFormLoading] = useState(false);

  const onSubmit = async (values: { email: string; password: string }) => {
    setFormLoading(true);
    try {
      await axios.post('/v1/auth', {
        username: values.email,
        password: values.password,
      });
    } catch (err) {
      setFormLoading(false);
      if (
        err?.isAxiosError &&
        err.response.status === 403 &&
        err.response.data.error === 'mfa_required'
      ) {
        const { method, state, target, userId } = err.response.data.data;
        const destination = globalThis?.location.href;
        const queryParams = new URLSearchParams({
          method,
          state,
          target,
          userId,
          action: MFAAction.LOGIN,
          destination,
          loginUrl: destination,
        });
        history.push(`/mfa?${queryParams}`);
        return;
      }

      if (err?.response?.status) {
        setFormError(err.response.data.message);
        return;
      }

      setFormError('There was an error with your Sign in. Please try again.');
      return;
    }

    setFormLoading(false);
    globalThis?.location.reload();
  };

  const validationSchema = yup.object().shape({
    email: yup.string().required(),
    password: yup.string().required(),
  });

  return (
    <ThemeProvider theme={theme}>
      <RightSideContent
        heading="Sign in to your account to trade with this supplier"
        footerActions={
          <>
            <SignedOutContainer>
              New to Ordermentum?{' '}
              <Button onClick={() => onPageChange('signUp')} variant="tertiary">
                Join now
              </Button>
            </SignedOutContainer>

            <ButtonWrapper>
              <Button
                disabled={isFormLoading}
                variant="primary"
                form="sign-in-form"
              >
                {isFormLoading ? <Spinner size="S" color="white" /> : 'Sign in'}
              </Button>
            </ButtonWrapper>
          </>
        }
      >
        <Content>
          <Formik
            initialValues={{ email: '', password: '' }}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {formik => (
              <form id="sign-in-form" onSubmit={formik.handleSubmit}>
                <Button
                  type="button"
                  variant="secondary"
                  full
                  onClick={onGoogleButtonClick}
                  icon={<img src={GOOGLE_IMAGE_URL} />}
                  className="border-text-strong text-strong"
                >
                  Sign in with Google
                </Button>
                <DividerContainer>
                  <Divider text="or" type="subtle" />
                </DividerContainer>
                <InputContainer>
                  <Input
                    inputLabel="Email"
                    size="max"
                    tag="email"
                    type="email"
                    value={formik.values.email}
                    onChange={formik.setFieldValue}
                    required
                  />
                  <Input
                    inputLabel="Password"
                    size="max"
                    tag="password"
                    type="password"
                    value={formik.values.password}
                    onChange={formik.setFieldValue}
                    required
                  />
                </InputContainer>
                {!!formError && (
                  <ErrorContainer>
                    <Banner variant="error">{formError}</Banner>
                  </ErrorContainer>
                )}
              </form>
            )}
          </Formik>
        </Content>
      </RightSideContent>
    </ThemeProvider>
  );
};

export default SignInForm;
